import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  // Enable swipe gestures for mobile
  swipingTransitionDuration: 300,
  slideDuration: 300,
  // Disable keyboard shortcuts on mobile
  disableKeyDown: false,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [galleryRef, setGalleryRef] = useState(null);
  const [modalGalleryRef, setModalGalleryRef] = useState(null);
  const [isMobileView, setIsMobileView] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth <= 1023 : false)
  );
  const intl = useIntl();
  const { rootClassName, className, images, imageVariants, thumbnailVariants } = props;
  const thumbVariants = thumbnailVariants || imageVariants;

  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);
  const items = images.map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
    };
  });
  const imageSizesMaybe = isModalOpen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };

  const handleImageClick = () => {
    if (!isModalOpen && galleryRef) {
      // Get current index from the main gallery before opening modal
      const currentIndex = galleryRef.getCurrentIndex();
      setCurrentImageIndex(currentIndex);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleBackdropClick = (e) => {
    // Close modal when clicking on the backdrop (outside the modal content)
    if (e.target === e.currentTarget) {
      handleCloseModal();
    }
  };

  // Handle escape key and body scroll
  useEffect(() => {
    if (isModalOpen) {
      // Disable body scroll
      document.body.style.overflow = 'hidden';
      
      // Handle escape key
      const handleEscapeKey = (e) => {
        if (e.key === 'Escape') {
          handleCloseModal();
        }
      };
      
      document.addEventListener('keydown', handleEscapeKey);
      
      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isModalOpen]);

  // Track viewport to detect mobile view
  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }

    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 1023);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleModalImageClick = () => {
    if (!isMobileView || !modalGalleryRef) {
      return;
    }

    const current = modalGalleryRef.getCurrentIndex();
    const nextIndex = (current + 1) % items.length;
    modalGalleryRef.slideToIndex(nextIndex);
  };

  const renderItem = (item, isModal = false) => {
    const isClickableModalImage = isModal && isMobileView;
    const cursorStyle = isModal ? (isClickableModalImage ? 'pointer' : 'default') : 'pointer';

    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={isModal ? css.itemWrapperModal : css.itemWrapper}
      >
        <div 
          className={css.itemCentering}
          onClick={
            isModal ? (isClickableModalImage ? handleModalImageClick : undefined) : handleImageClick
          }
          style={{ cursor: cursorStyle }}
        >
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            {...imageSizesMaybe}
          />
        </div>
      </AspectRatioWrapper>
    );
  };

  const renderThumbInner = item => {
    const originalImage = item?.image?.attributes?.variants?.['scaled-small']?.url;
    return (
      <div>
        {/* <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}

        /> */}
        <img className={css.thumb} src={originalImage} alt={item.thumbAlt} sizes="88px" />
      </div>
    );
  };

  // Render function for modal gallery items
  const renderModalItem = item => renderItem(item, true);

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick} aria-label="Previous image">
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick} aria-label="Next image">
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  
  // For modal gallery, show arrows
  const renderModalLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeftModal} disabled={disabled} onClick={onClick} aria-label="Previous image">
        <div className={css.navArrowWrapperModal}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  
  const renderModalRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRightModal} disabled={disabled} onClick={onClick} aria-label="Next image">
        <div className={css.navArrowWrapperModal}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);
  const modalClasses = classNames(css.modalGallery);

  return (
    <>
      {/* Main gallery - click to open modal */}
      <ReactImageGallery
        ref={ref => setGalleryRef(ref)}
        additionalClass={classes}
        items={items}
        renderItem={renderItem}
        renderThumbInner={renderThumbInner}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        showFullscreenButton={false}
        {...IMAGE_GALLERY_OPTIONS}
      />

      {/* Modal gallery with larger images and navigation */}
      {isModalOpen && (
        <div className={css.modalBackdrop} onClick={handleBackdropClick}>
          <div className={css.modalWrapper}>
            <Button
              onClick={handleCloseModal}
              rootClassName={css.modalClose}
              title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
            >
              <span className={css.closeText}>
                <FormattedMessage id="ListingImageGallery.closeModal" />
              </span>
              <IconClose rootClassName={css.closeIcon} />
            </Button>
            <div className={css.modalGalleryWrapper} onClick={(e) => e.stopPropagation()}>
              <ReactImageGallery
                ref={ref => setModalGalleryRef(ref)}
                additionalClass={modalClasses}
                items={items}
                startIndex={currentImageIndex}
                renderItem={renderModalItem}
                renderLeftNav={renderModalLeftNav}
                renderRightNav={renderModalRightNav}
                showFullscreenButton={false}
                showThumbnails={false}
                {...IMAGE_GALLERY_OPTIONS}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ListingImageGallery;
